import React, { useCallback, useState, useLayoutEffect } from "react";
import { DragDropContext, Draggable, DropResult } from "react-beautiful-dnd";
import classnames from "classnames";
import styles from "./draggable-list.module.scss";
import { StrictModeDroppable } from "./StrictModeDroppable";
type ElementProps = React.ReactNode & { key: string };

export const DraggableListComponent: React.FC<{
  onDragEnd: (data: { oldListKeys: string[]; newListKeys: string[] }) => void;
  children?: React.ReactNode;
}> = ({ children, onDragEnd }) => {
  const [list, setList] = useState<{ element: ElementProps; key: string; position: number }[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const onDragEndHandler = useCallback(
    (result: DropResult) => {
      setIsDragging(false);
      if (!result.destination) return;
      const newList = Array.from(list);
      const [removed] = newList.splice(result.source.index, 1);
      newList.splice(result.destination.index, 0, removed);
      onDragEnd({ oldListKeys: list.map((item) => item.key), newListKeys: newList.map((item) => item.key) });
    },
    [list, onDragEnd]
  );

  const onDragStartHandler = useCallback(() => {
    setIsDragging(true);
  }, []);

  useLayoutEffect(() => {
    const newChildrenList = React.Children.toArray(children).map((element, index) => ({
      element: element as ElementProps,
      key: (element as ElementProps).key.replace(".$", ""),
      position: index,
    }));
    setList(newChildrenList);
  }, [children]);

  if (list.length === 0) return <></>;

  return (
    <DragDropContext onDragEnd={onDragEndHandler} onDragStart={onDragStartHandler}>
      <StrictModeDroppable droppableId={`droppable`}>
        {(provided) => (
          <div {...provided.droppableProps} ref={provided.innerRef} className={classnames(styles.list)}>
            {list.map((item, index) => (
              <Draggable key={item.key} draggableId={item.key} index={index}>
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    style={{ ...provided.draggableProps.style, transition: !isDragging ? "none" : provided.draggableProps.style?.transition }}
                    className={classnames(styles.draggableItemContainer)}
                  >
                    <div>
                      <div className={classnames(styles.draggableDotsContainer)}>
                        <div className={styles.draggableDots}>...</div>
                        <div className={styles.draggableDots}>...</div>
                      </div>
                      {item.element}
                    </div>
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </StrictModeDroppable>
    </DragDropContext>
  );
};
